// biome-ignore-all lint/correctness/useHookAtTopLevel: TODO - address later
// biome-ignore-all lint/performance/noAccumulatingSpread: TODO - address later
import { api } from "@/lib/api";
import type { MetricsFilters } from "@/lib/filters";

export interface WebsitePageMetrics {
  // General engagement metrics
  total_engagements?: number;

  // Person metrics
  people_engaged?: number;
  engagements_per_person?: number;

  // Account metrics
  accounts_engaged?: number;
  accounts_activated?: number;
  accounts_reactivated?: number;
  accounts_without_opps?: number;
  engagements_per_account?: number;

  // Deal metrics
  deal_contacts_engaged?: number;
  engagements_per_deal_contact?: number;

  // Opportunity metrics
  opportunities_influenced?: number;
  opportunities_influenced_amount?: number;
  opportunities_influenced_won?: number;
  opportunities_influenced_won_amount?: number;
  opportunities_activated?: number;
  opportunities_activated_amount?: number;
  opportunities_activated_won?: number;
  opportunities_activated_won_amount?: number;
  opportunities_created_post_engagement?: number;
  opportunities_created_post_engagement_amount?: number;
  opportunities_influenced_in_cycle?: number;
  opportunities_influenced_in_cycle_amount?: number;
  progressed_opportunities?: number;
  progressed_opportunities_amount?: number;

  // Timing metrics
  first_engagement_date?: string;
  last_engagement_date?: string;
  days_since_first_engagement?: number;
  days_since_latest_engagement?: number;
  webpage_active_days?: number;
}

// Drilldown interfaces
export interface PageViewDrilldown {
  timestamp: string;
  person_id: string;
  session_id: string;
  web_session_channel: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  account_name?: string;
  account_id?: string;
}

export interface PersonEngagedDrilldown {
  person_id: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  account_name?: string;
  account_id?: string;
  total_engagements: number;
  first_engagement_date: string;
  last_engagement_date: string;
}

export interface AccountEngagedDrilldown {
  account_id: string;
  account_name: string;
  people_count: number;
  total_engagements: number;
  first_engagement_date: string;
  last_engagement_date: string;
}

export interface OpportunityInfluencedDrilldown {
  opportunity_id: string;
  opportunity_name: string;
  opportunity_stage: string;
  amount: number;
  is_won: boolean;
  is_closed: boolean;
  account_name?: string;
  account_id?: string;
  people_engaged: number;
  total_engagements: number;
  first_engagement_date: string;
  last_engagement_date: string;
}

export interface OpportunityActivatedDrilldown {
  opportunity_id: string;
  opportunity_source_record_id: string;
  opportunity_name: string;
  account_name?: string;
  account_id?: string;
  account_source_record_id?: string;
  current_stage: string;
  created_date: string;
  close_date?: string;
  amount: number;
  is_won: boolean;
  analysis_type: string;
  analysis_created_date: string;
  influence_source: string;
  summary: string;
  people_engaged: number;
  total_engagements: number;
  first_engagement_date?: string;
  last_engagement_date?: string;
}

export interface DealContactEngagedDrilldown {
  person_id: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
  account_name?: string;
  account_id?: string;
  opportunity_count: number;
  total_engagements: number;
  first_engagement_date: string;
  last_engagement_date: string;
}

export type DrilldownData =
  | PageViewDrilldown[]
  | PersonEngagedDrilldown[]
  | AccountEngagedDrilldown[]
  | OpportunityInfluencedDrilldown[]
  | OpportunityActivatedDrilldown[]
  | DealContactEngagedDrilldown[];

interface UseWebsitePageMetricsOptions {
  url: string | null;
  metrics: (keyof WebsitePageMetrics)[];
  channelFilter?: string;
  startDate?: string;
  endDate?: string;
  filters?: MetricsFilters;
  enabled?: boolean;
}

interface UseWebsitePageMetricDrilldownOptions {
  url: string | null;
  metric: keyof WebsitePageMetrics;
  channelFilter?: string;
  startDate?: string;
  endDate?: string;
  filters?: MetricsFilters;
  enabled?: boolean;
  limit?: number;
  offset?: number;
}

interface MetricLoadingState {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

interface UseWebsitePageMetricsResult {
  data: WebsitePageMetrics;
  isLoading: boolean;
  loadingStates: Record<keyof WebsitePageMetrics, MetricLoadingState>;
}

// Map of which metrics are provided by which query groups
const METRIC_GROUPS = {
  basic_engagement: new Set([
    "total_engagements",
    "people_engaged",
    "engagements_per_person",
  ]),
  response_timing: new Set([
    "first_engagement_date",
    "last_engagement_date",
    "days_since_first_engagement",
    "days_since_latest_engagement",
    "webpage_active_days",
  ]),
  account_metrics: new Set([
    "accounts_engaged",
    "accounts_activated",
    "accounts_reactivated",
    "accounts_without_opps",
    "engagements_per_account",
  ]),
  opportunity_metrics: new Set([
    "opportunities_influenced",
    "opportunities_influenced_amount",
    "opportunities_influenced_won",
    "opportunities_influenced_won_amount",
    "opportunities_influenced_in_cycle",
    "opportunities_influenced_in_cycle_amount",
    "opportunities_created_post_engagement",
    "opportunities_created_post_engagement_amount",
    "deal_contacts_engaged",
    "engagements_per_deal_contact",
  ]),
  opportunities_activated: new Set([
    "opportunities_activated",
    "opportunities_activated_amount",
    "opportunities_activated_won",
    "opportunities_activated_won_amount",
  ]),
  progressed_opportunities: new Set([
    "progressed_opportunities",
    "progressed_opportunities_amount",
  ]),
} as const;

// Map each metric to its group
const METRIC_TO_GROUP = Object.entries(METRIC_GROUPS).reduce(
  (acc, [group, metrics]) => {
    (metrics as Set<string>).forEach((metric) => {
      acc[metric] = group;
    });
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Hook to fetch website page metrics with support for metric grouping and parallel fetching
 */
export function useWebsitePageMetrics({
  url,
  metrics,
  channelFilter,
  startDate,
  endDate,
  filters,
  enabled = true,
}: UseWebsitePageMetricsOptions): UseWebsitePageMetricsResult {
  // Group metrics by their query group
  const metricsByGroup = metrics.reduce(
    (acc, metric) => {
      const group = METRIC_TO_GROUP[metric];
      if (group) {
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push(metric);
      }
      return acc;
    },
    {} as Record<string, Array<keyof WebsitePageMetrics>>,
  );

  // Create a query for each group that has metrics requested
  const queries = Object.entries(metricsByGroup).map(([, groupMetrics]) => {
    const searchParams = new URLSearchParams();

    // Add URL as a query parameter
    searchParams.append("url", url || "");

    // Add metrics as comma-separated string
    searchParams.append("metrics", groupMetrics.join(","));

    // Add optional filters
    if (channelFilter) searchParams.append("channel_id", channelFilter);
    if (startDate) searchParams.append("start_date", startDate);
    if (endDate) searchParams.append("end_date", endDate);

    // Serialize filters object to JSON and add as metric_filters parameter
    if (filters && Object.keys(filters).length > 0) {
      searchParams.append("metric_filters", JSON.stringify(filters));
    }

    const queryUrl = `datahub/webpages/metrics/?${searchParams}`;

    return {
      metrics: groupMetrics,
      query: api.useQueryWithTransform<
        | { url: string; metrics: WebsitePageMetrics; filters: MetricsFilters }
        | WebsitePageMetrics,
        WebsitePageMetrics
      >(
        queryUrl,
        (data) => {
          // Support both REST (metrics object directly) and gRPC-transformed shape
          if (
            data &&
            typeof data === "object" &&
            "metrics" in (data as Record<string, unknown>)
          ) {
            return (data as { metrics: WebsitePageMetrics }).metrics;
          }
          return data as WebsitePageMetrics;
        },
        {
          enabled: enabled && !!url,
        },
      ),
    };
  });

  // Initialize loading states for all requested metrics
  const loadingStates = metrics.reduce(
    (acc, metric) => {
      acc[metric] = {
        isLoading: true,
        isError: false,
        error: null,
      };
      return acc;
    },
    {} as Record<keyof WebsitePageMetrics, MetricLoadingState>,
  );

  // Update loading states and combine data from all queries
  const data = queries.reduce((acc, { metrics: groupMetrics, query }) => {
    // Update loading states for this group's metrics
    groupMetrics.forEach((metric) => {
      loadingStates[metric] = {
        isLoading: query.isLoading,
        isError: !!query.error,
        error: query.error,
      };
    });

    // Add this group's data if available
    if (query.data) {
      // Query data already transformed to WebsitePageMetrics
      return { ...acc, ...query.data };
    }
    return acc;
  }, {} as WebsitePageMetrics);

  // Overall loading state is true if any metric is still loading
  const isLoading = Object.values(loadingStates).some(
    (state) => state.isLoading,
  );

  return {
    data,
    isLoading,
    loadingStates,
  };
}

/**
 * Hook to fetch drilldown data for a specific website page metric
 */
export function useWebsitePageMetricDrilldown({
  url,
  metric,
  channelFilter,
  startDate,
  endDate,
  filters,
  enabled = true,
  limit = 100,
  offset = 0,
}: UseWebsitePageMetricDrilldownOptions) {
  const searchParams = new URLSearchParams();

  // Add URL as a query parameter (required)
  searchParams.append("url", url || "");

  // Add query parameters
  searchParams.append("limit", limit.toString());
  searchParams.append("offset", offset.toString());

  // Add optional filters
  if (channelFilter) searchParams.append("channel_id", channelFilter);
  if (startDate) searchParams.append("start_date", startDate);
  if (endDate) searchParams.append("end_date", endDate);

  // Serialize filters object to JSON and add as metric_filters parameter
  if (filters && Object.keys(filters).length > 0) {
    searchParams.append("metric_filters", JSON.stringify(filters));
  }

  return api.useQuery<{
    url: string;
    metric: string;
    results: DrilldownData;
    count: number;
    limit: number;
    offset: number;
  }>(`datahub/webpages/metrics/${metric}/drilldown/?${searchParams}`, {
    enabled: enabled && !!url,
  });
}

// Legacy support - maintain backward compatibility
export interface WebsitePageMetricsResponse {
  url: string;
  metrics: Partial<WebsitePageMetrics>;
  filters: {
    channel_id: string;
    start_date?: string;
    end_date?: string;
  };
}

/**
 * @deprecated Use useWebsitePageMetrics instead
 */
export function useWebsitePageMetricsLegacy({
  url,
  channelId = "all",
  startDate,
  endDate,
  metrics = ["total_engagements", "people_engaged", "accounts_engaged"],
}: {
  url: string;
  channelId?: string;
  startDate?: string;
  endDate?: string;
  metrics?: string[];
}) {
  const params = new URLSearchParams();
  params.append("url", url);
  if (channelId && channelId !== "all") params.append("channel_id", channelId);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  if (metrics.length > 0) params.append("metrics", metrics.join(","));

  return api.useQuery<WebsitePageMetricsResponse>(
    `datahub/webpages/metrics/?${params.toString()}`,
    {
      enabled: !!url,
    },
  );
}
